import React, { useState, useEffect, useRef } from "react";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";
import { Helmet } from "react-helmet";

// Question Type Definitions
interface BaseQuestion {
    type: string;
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    correct: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[]; // Fill-in options for user to choose
    correct: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    correct: boolean;
}

interface MatchThePairQuestion extends BaseQuestion {
    type: "match";
    options: string[];
    labeled: { "A": string[], "B": string[] };
    correct: string;
}

// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion
    | MatchThePairQuestion;

// Define types
type SyllabusType =
    | "All Chapters"


interface MultipleChoiceQuestion {
    question: string;
    options: string[];
    correct: string;
    syllabus?: SyllabusType;
}



const syllabusFiles: Record<SyllabusType, string> = {
    "All Chapters": "SeasonsforLKG.json"
}

const Seasons: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {
        const topics = Object.keys(syllabusFiles) as SyllabusType[];
        const randomTopic = topics[Math.floor(Math.random() * topics.length)];
        const filename = syllabusFiles[randomTopic];

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "/" + filename
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/${filename}`
                );
                data = module.default;
            }
            setQuestions(shuffleArray(data));
            const choosedOne = data[Math.floor(Math.random() * data.length)];
            setCurrentQuestion(choosedOne);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.correct;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false" || currentQuestion.type === "match") {
            correctAnswer = currentQuestion.correct;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                setShowImage(false);
                loadRandomSyllabusQuestions();
                setSelectedOption(null);
            }, 4000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Seasons Worksheet for LKG | Learn Summer, Winter, Rainy with Fun Activities";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach LKG kids about the seasons with colorful worksheets. Learn about summer, winter, rainy season and more with matching, coloring, and identifying activities." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Learn About Seasons</h1>

                            <p>
                                Seasons change the way we dress, eat, play, and enjoy nature. This set of <strong>season-themed worksheets</strong> is designed to introduce LKG students to the concept of different <strong>weather seasons</strong> like summer, winter, rainy, and spring. Using fun visuals and simple activities, children can easily identify and relate to each season.
                            </p>

                            <h2>🌦️ Why Teach Seasons in LKG?</h2>
                            <p>
                                Children observe weather changes around them, and teaching seasons helps them:
                                <ul>
                                    <li>Understand weather patterns</li>
                                    <li>Identify seasonal clothes, food, and activities</li>
                                    <li>Build vocabulary and observation skills</li>
                                </ul>
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify four main seasons: <strong>Summer, Winter, Rainy, and Spring</strong></li>
                                <li>Match seasonal clothes and objects</li>
                                <li>Recognize seasonal changes in the environment</li>
                                <li>Relate to festivals, food, and activities of each season</li>
                            </ul>

                            <h2>📋 Worksheet Activities</h2>
                            <ul>
                                <li>Match season with appropriate clothes or objects</li>
                                <li>Color the pictures that belong to each season</li>
                                <li>Guess the season based on given clues or visuals</li>
                                <li>Circle the correct seasonal food/item</li>
                                <li>Fill in the blanks with season names</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Use stories and rhymes related to seasons</li>
                                <li>Show real clothes and fruits used in each season</li>
                                <li>Display a weather chart or calendar in the classroom</li>
                                <li>Take children for nature walks to observe seasonal changes</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/general-awareness/people-who-help-us"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>People Who Helps Us</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/general-awareness/good-habits"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Good Habits & Manners</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/general-awareness/transports"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Transports</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Dress your child appropriately each morning and talk about which season it is and why – it makes learning practical and fun!
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Learn About Seasons</h1>

                        <p>
                            Seasons change the way we dress, eat, play, and enjoy nature. This set of <strong>season-themed worksheets</strong> is designed to introduce LKG students to the concept of different <strong>weather seasons</strong> like summer, winter, rainy, and spring. Using fun visuals and simple activities, children can easily identify and relate to each season.
                        </p>

                        <h2>🌦️ Why Teach Seasons in LKG?</h2>
                        <p>
                            Children observe weather changes around them, and teaching seasons helps them:
                            <ul>
                                <li>Understand weather patterns</li>
                                <li>Identify seasonal clothes, food, and activities</li>
                                <li>Build vocabulary and observation skills</li>
                            </ul>
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Identify four main seasons: <strong>Summer, Winter, Rainy, and Spring</strong></li>
                            <li>Match seasonal clothes and objects</li>
                            <li>Recognize seasonal changes in the environment</li>
                            <li>Relate to festivals, food, and activities of each season</li>
                        </ul>

                        <h2>📋 Worksheet Activities</h2>
                        <ul>
                            <li>Match season with appropriate clothes or objects</li>
                            <li>Color the pictures that belong to each season</li>
                            <li>Guess the season based on given clues or visuals</li>
                            <li>Circle the correct seasonal food/item</li>
                            <li>Fill in the blanks with season names</li>
                        </ul>

                        <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                        <ul>
                            <li>Use stories and rhymes related to seasons</li>
                            <li>Show real clothes and fruits used in each season</li>
                            <li>Display a weather chart or calendar in the classroom</li>
                            <li>Take children for nature walks to observe seasonal changes</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/lkg/general-awareness/people-who-help-us"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>People Who Helps Us</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/general-awareness/good-habits"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Good Habits & Manners</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/general-awareness/transports"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Transports</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🌟 Mama’s Guidance Tip</h2>
                        <p>
                            Dress your child appropriately each morning and talk about which season it is and why – it makes learning practical and fun!
                        </p>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.correct.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.correct).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                            {currentQuestion.type === "match" && (
                                <>
                                    <table className="table-bordered table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Column A</th>
                                                <th scope="col">Column B</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentQuestion.labeled.A.map((leftItem, index) => (
                                                <tr key={index}>
                                                    <td>{leftItem}</td>
                                                    <td>{currentQuestion.labeled.B[index]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.correct.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                </>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default Seasons;
